import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { PageHeader, Pill } from "../components/Bits";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { CheckCircleIcon, XCircleIcon, PlusIcon, TrashIcon, ShieldCheckIcon, PhoneCallIcon, ArrowsClockwiseIcon, CaretLeftIcon, CaretRightIcon, GlobeIcon } from "@phosphor-icons/react";
import { formatDate, formatRelative } from "../lib/format";

const PAGE_SIZE = 10;

const ACTION_COLORS = {
    create: "#059669",
    update: "#0033A0",
    delete: "#E02424",
    upload: "#0EA5E9",
    start: "#059669",
    pause: "#D97706",
    signup: "#111827",
    call_completed: "#0033A0",
    call_placed: "#0033A0",
};

export default function Settings() {
    const [status, setStatus] = useState(null);
    const [dnc, setDnc] = useState([]);
    const [audit, setAudit] = useState({ items: [], total: 0 });
    const [auditPage, setAuditPage] = useState(0);
    const [auditLoading, setAuditLoading] = useState(false);
    const [phone, setPhone] = useState("");
    const [vapiNumbers, setVapiNumbers] = useState([]);
    const [vapiSelected, setVapiSelected] = useState("");
    const [vapiBusy, setVapiBusy] = useState(false);
    const [twilioForm, setTwilioForm] = useState({ account_sid: "", auth_token: "", phone_number: "", name: "" });
    const [twilioBusy, setTwilioBusy] = useState(false);
    const [refreshTarget, setRefreshTarget] = useState(null); // vapi phone-number object being refreshed
    const [refreshForm, setRefreshForm] = useState({ account_sid: "", auth_token: "" });
    const [refreshBusy, setRefreshBusy] = useState(false);

    const loadAudit = useCallback(async (page) => {
        setAuditLoading(true);
        try {
            const { data } = await api.get("/audit-log", { params: { limit: PAGE_SIZE, skip: page * PAGE_SIZE } });
            setAudit({ items: data.items || [], total: data.total || 0 });
        } finally { setAuditLoading(false); }
    }, []);

    const load = async () => {
        const [s, d, vp] = await Promise.all([
            api.get("/integrations/status"),
            api.get("/dnc"),
            api.get("/integrations/vapi/phone-numbers"),
        ]);
        setStatus(s.data); setDnc(d.data);
        setVapiNumbers(vp.data.phone_numbers || []);
        setVapiSelected(vp.data.selected_id || "");
    };

    useEffect(() => {
        const init = async () => {
            await load();
            await loadAudit(0);
        };
        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const goToAuditPage = async (page) => {
        setAuditPage(page);
        await loadAudit(page);
    };

    const refreshVapi = async () => {
        setVapiBusy(true);
        try {
            const { data } = await api.get("/integrations/vapi/phone-numbers");
            setVapiNumbers(data.phone_numbers || []);
            setVapiSelected(data.selected_id || "");
            toast.success("Refreshed Vapi phone numbers");
        } finally { setVapiBusy(false); }
    };

    const saveVapi = async () => {
        setVapiBusy(true);
        try {
            await api.put("/integrations/vapi/settings", { vapi_phone_number_id: vapiSelected || null });
            toast.success(vapiSelected ? "Vapi phone number connected" : "Vapi phone number cleared");
            await load();
        } catch (e) {
            toast.error("Failed to save");
        } finally { setVapiBusy(false); }
    };

    const importTwilio = async () => {
        const { account_sid, auth_token, phone_number, name } = twilioForm;
        if (!account_sid.trim() || !auth_token.trim() || !phone_number.trim()) {
            toast.error("Account SID, Auth Token, and phone number are required");
            return;
        }
        setTwilioBusy(true);
        try {
            const { data } = await api.post("/integrations/twilio/import", {
                account_sid: account_sid.trim(),
                auth_token: auth_token.trim(),
                phone_number: phone_number.trim(),
                name: name.trim() || null,
            });
            toast.success(`Imported ${data.number}`, { description: data.auto_selected ? "Auto-selected as your active outbound number." : "Pick it below to use it." });
            setTwilioForm({ account_sid: "", auth_token: "", phone_number: "", name: "" });
            await load();
        } catch (e) {
            const detail = e?.response?.data?.detail || "Twilio import failed";
            toast.error(typeof detail === "string" ? detail : "Twilio import failed");
        } finally { setTwilioBusy(false); }
    };

    const deleteVapiNumber = async (numId, label) => {
        if (!window.confirm(`Remove ${label} from this workspace? You can re-import later.`)) return;
        try {
            await api.delete(`/integrations/vapi/phone-numbers/${numId}`);
            toast.success("Number removed");
            await load();
        } catch {
            toast.error("Failed to remove number");
        }
    };

    const submitRefresh = async () => {
        if (!refreshTarget) return;
        const sid = refreshForm.account_sid.trim();
        const token = refreshForm.auth_token.trim();
        if (!sid.startsWith("AC") || token.length < 20) {
            toast.error("Provide a valid Twilio Account SID (AC...) and Auth Token");
            return;
        }
        setRefreshBusy(true);
        try {
            await api.post("/integrations/twilio/refresh", {
                phone_number_id: refreshTarget.id,
                account_sid: sid,
                auth_token: token,
            });
            toast.success(`${refreshTarget.name || refreshTarget.number} credentials refreshed`);
            setRefreshTarget(null);
            setRefreshForm({ account_sid: "", auth_token: "" });
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Refresh failed");
        } finally {
            setRefreshBusy(false);
        }
    };

    const addDnc = async () => {
        if (!phone.trim()) return;
        await api.post("/dnc", { phone, reason: "Manually added" });
        setPhone("");
        toast.success("Added to DNC");
        load();
    };

    const removeDnc = async (id) => {
        await api.delete(`/dnc/${id}`);
        toast.success("Removed");
        load();
    };

    const realNumbers = vapiNumbers.filter((n) => n.has_number);

    return (
        <div className="pb-12">
            <PageHeader overline="WORKSPACE" title="Settings" description="Integrations, compliance, and audit." testid="settings-header" />

            <div className="px-8 py-6 grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Vapi panel */}
                <div className="swiss-section lg:col-span-2" data-testid="vapi-panel">
                    <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between gap-3 flex-wrap">
                        <div>
                            <div className="overline">OUTBOUND TELEPHONY</div>
                            <h3 className="text-xl font-semibold tracking-tight mt-0.5">Vapi · phone number</h3>
                            <p className="text-[12px] text-[var(--text-secondary)] mt-1 max-w-xl">
                                Pick a number from your Vapi account. Calls placed by this workspace will originate from
                                the selected number. Until a number is selected, campaigns run in MOCK mode (Replicate-generated transcripts).
                            </p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Pill color={status?.vapi?.ready ? "#059669" : (status?.vapi?.connected ? "#D97706" : "#6B7280")}>
                                {status?.vapi?.ready ? "Ready" : (status?.vapi?.connected ? "Pick number" : "Not connected")}
                            </Pill>
                            <button type="button" onClick={refreshVapi} disabled={vapiBusy} className="btn-ghost px-3 py-1.5 text-[12px] flex items-center gap-1.5" data-testid="vapi-refresh">
                                <ArrowsClockwiseIcon size={12} weight="bold" /> Refresh
                            </button>
                        </div>
                    </div>
                    <div className="px-5 py-4">
                        {realNumbers.length === 0 ? (
                            <p className="text-[13px] text-[var(--text-secondary)]">
                                No phone numbers with a dialable number found. Provision one at{" "}
                                <a href="https://dashboard.vapi.ai/phone-numbers" target="_blank" rel="noreferrer" className="underline text-[var(--text-primary)]">dashboard.vapi.ai</a>.
                            </p>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="vapi-numbers-grid">
                                {realNumbers.map((n) => (
                                    <label
                                        key={n.id}
                                        className={`flex items-start gap-3 px-4 py-3 border cursor-pointer transition-colors ${
                                            vapiSelected === n.id ? "border-[var(--primary)] bg-[var(--surface-active)]" : "border-[var(--border)] hover:bg-[var(--surface-active)]"
                                        }`}
                                        data-testid={`vapi-number-${n.id}`}
                                    >
                                        <input
                                            type="radio"
                                            name="vapi-number"
                                            checked={vapiSelected === n.id}
                                            onChange={() => setVapiSelected(n.id)}
                                            className="mt-1"
                                            data-testid={`vapi-number-radio-${n.id}`}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-[14px] flex items-center gap-2 flex-wrap">
                                                <PhoneCallIcon size={14} weight="bold" /> {n.name}
                                                {n.provider === "twilio" && <span className="overline text-[9px] px-1.5 py-0.5 border border-[var(--accent)] text-[var(--accent)]">TWILIO</span>}
                                                {n.provider === "vapi" && <span className="overline text-[9px] px-1.5 py-0.5 border border-[var(--text-secondary)] text-[var(--text-secondary)]">VAPI</span>}
                                            </div>
                                            <div className="mono text-[13px] mt-0.5">{n.number}</div>
                                            <div className="overline text-[9px] mt-1">{n.provider} · {n.status}</div>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            {n.provider === "twilio" && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.preventDefault(); setRefreshTarget(n); setRefreshForm({ account_sid: "", auth_token: "" }); }}
                                                    className="text-[var(--text-secondary)] hover:text-[var(--accent)] p-1 transition-colors"
                                                    title="Rotate the Twilio Auth Token — delete + re-import in one click"
                                                    data-testid={`twilio-refresh-${n.id}`}
                                                >
                                                    <ArrowsClockwiseIcon size={14} weight="bold" />
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={(e) => { e.preventDefault(); deleteVapiNumber(n.id, n.number || n.name); }}
                                                className="text-[var(--text-secondary)] hover:text-[var(--live)] p-1 transition-colors"
                                                title="Remove number from workspace"
                                                data-testid={`vapi-number-delete-${n.id}`}
                                            >
                                                <TrashIcon size={14} weight="bold" />
                                            </button>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}
                        <div className="flex items-center gap-2 mt-4">
                            <button
                                type="button"
                                onClick={saveVapi}
                                disabled={vapiBusy}
                                className="btn-primary px-4 py-2 text-[13px]"
                                data-testid="vapi-save"
                            >
                                {vapiBusy ? "Saving…" : "Save selection"}
                            </button>
                            {vapiSelected && (
                                <button type="button" onClick={() => { setVapiSelected(""); }} className="btn-ghost px-3 py-2 text-[12px]" data-testid="vapi-clear">
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Twilio BYO panel */}
                <div className="swiss-section lg:col-span-2" data-testid="twilio-panel">
                    <div className="px-5 py-4 border-b border-[var(--border)]">
                        <div className="overline text-[var(--accent)] flex items-center gap-1.5">
                            <GlobeIcon size={11} weight="bold" /> GLOBAL OUTBOUND
                        </div>
                        <h3 className="text-xl font-semibold tracking-tight mt-0.5">Connect your Twilio number</h3>
                        <p className="text-[12px] text-[var(--text-secondary)] mt-1 max-w-2xl">
                            Import a phone number you already own in Twilio to enable international outbound calls.
                            Get your <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="underline text-[var(--text-primary)]">Account SID</a> and Auth Token from the Twilio Console (Account → API keys & tokens).
                            We send these to Vapi, which then routes calls through your Twilio trunk. Credentials are stored only inside Vapi — not in our database.
                        </p>
                        <div className="mt-3 px-3 py-2 border border-[var(--warning)] bg-[var(--warning)]/5 text-[12px] leading-relaxed" data-testid="twilio-rotate-hint">
                            <div className="overline text-[var(--warning)] text-[9px] mb-1">ROTATING YOUR AUTH TOKEN?</div>
                            <ol className="list-decimal pl-4 text-[var(--text-secondary)] space-y-0.5">
                                <li>Rotate the token in the <a href="https://console.twilio.com" target="_blank" rel="noreferrer" className="underline text-[var(--text-primary)]">Twilio Console</a> → Account → API keys &amp; tokens.</li>
                                <li>Remove the existing imported number above (trash icon) — this clears Vapi&apos;s cached old token.</li>
                                <li>Re-import the same Twilio number here with the <strong className="text-[var(--text-primary)]">new</strong> Auth Token.</li>
                            </ol>
                        </div>
                    </div>
                    <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="md:col-span-2">
                            <label className="overline mb-1.5 block">Twilio Account SID</label>
                            <input
                                value={twilioForm.account_sid}
                                onChange={(e) => setTwilioForm({ ...twilioForm, account_sid: e.target.value })}
                                placeholder="AC6805cab5913fe82fccf22f34f3d9755d"
                                className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] focus:border-[var(--accent)] outline-none text-[13px] mono"
                                data-testid="twilio-sid-input"
                            />
                        </div>
                        <div>
                            <label className="overline mb-1.5 block">Twilio Auth Token</label>
                            <input
                                type="password"
                                value={twilioForm.auth_token}
                                onChange={(e) => setTwilioForm({ ...twilioForm, auth_token: e.target.value })}
                                placeholder="••••••••••••••••••••••••••••••••"
                                className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] focus:border-[var(--accent)] outline-none text-[13px] mono"
                                data-testid="twilio-token-input"
                                autoComplete="off"
                            />
                        </div>
                        <div>
                            <label className="overline mb-1.5 block">Twilio Phone Number</label>
                            <input
                                value={twilioForm.phone_number}
                                onChange={(e) => setTwilioForm({ ...twilioForm, phone_number: e.target.value })}
                                placeholder="+13125550100"
                                className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] focus:border-[var(--accent)] outline-none text-[13px] mono"
                                data-testid="twilio-number-input"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="overline mb-1.5 block">Label (optional)</label>
                            <input
                                value={twilioForm.name}
                                onChange={(e) => setTwilioForm({ ...twilioForm, name: e.target.value })}
                                placeholder="e.g. US Sales Line"
                                className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] focus:border-[var(--accent)] outline-none text-[13px]"
                                data-testid="twilio-name-input"
                            />
                        </div>
                        <div className="md:col-span-2 flex items-center gap-2">
                            <button
                                type="button"
                                onClick={importTwilio}
                                disabled={twilioBusy}
                                className="btn-primary px-4 py-2 text-[13px] flex items-center gap-2 disabled:opacity-50"
                                data-testid="twilio-import-button"
                            >
                                <GlobeIcon size={14} weight="bold" /> {twilioBusy ? "Importing…" : "Import to Vapi"}
                            </button>
                            <span className="text-[11px] text-[var(--text-secondary)]">
                                Imported numbers appear in the Vapi picker above.
                            </span>
                        </div>
                    </div>
                </div>



                <div className="swiss-section" data-testid="integrations-panel">
                    <div className="px-5 py-4 border-b border-[var(--border)]">
                        <div className="overline">INTEGRATIONS</div>
                        <h3 className="text-xl font-semibold tracking-tight mt-0.5">Connected services</h3>
                    </div>
                    <ul className="grid-borders">
                        <Integration
                            id="replicate"
                            name="Replicate"
                            description="LLM transcript analysis & sentiment via Llama 3 70B."
                            connected={status?.replicate?.connected}
                        />
                        <Integration
                            id="vapi"
                            name="Vapi"
                            description={status?.vapi?.ready ? "Outbound calls live with selected phone number." : "API key set; pick a phone number above to go live."}
                            connected={status?.vapi?.ready}
                            badge={status?.vapi?.connected && !status?.vapi?.ready ? "PICK NUMBER" : undefined}
                        />
                        <Integration
                            id="gmail"
                            name="Gmail"
                            description="Send whitepapers from your own Gmail. Requires Google OAuth."
                            connected={status?.gmail?.connected}
                        />
                        <Integration
                            id="google_calendar"
                            name="Google Calendar"
                            description="Auto-create appointment invites for booked meetings."
                            connected={status?.google_calendar?.connected}
                        />
                    </ul>
                </div>

                <div className="swiss-section" data-testid="dnc-panel">
                    <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between">
                        <div>
                            <div className="overline">COMPLIANCE</div>
                            <h3 className="text-xl font-semibold tracking-tight mt-0.5">Do Not Call list</h3>
                        </div>
                        <ShieldCheckIcon size={18} weight="bold" className="text-[var(--success)]" />
                    </div>
                    <div className="px-5 py-3 border-b border-[var(--border)] flex items-center gap-2">
                        <input
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="+14155550100"
                            className="flex-1 px-3 py-2 border border-[var(--border)] rounded-[2px] focus:border-[var(--accent)] outline-none text-[13px] mono"
                            data-testid="dnc-phone-input"
                        />
                        <button type="button" onClick={addDnc} className="btn-primary px-3 py-2 text-[12px] flex items-center gap-1.5" data-testid="dnc-add-button">
                            <PlusIcon size={12} weight="bold" /> Add
                        </button>
                    </div>
                    {dnc.length === 0 ? (
                        <div className="p-6 text-center text-[var(--text-secondary)] text-[13px]">DNC list is empty.</div>
                    ) : (
                        <ul className="grid-borders" data-testid="dnc-list">
                            {dnc.map((d) => (
                                <li key={d.id} className="px-5 py-3 flex items-center justify-between gap-3">
                                    <div>
                                        <div className="mono font-semibold">{d.phone}</div>
                                        {d.reason && <div className="text-[11px] text-[var(--text-secondary)] mt-0.5">{d.reason}</div>}
                                    </div>
                                    <button type="button" onClick={() => removeDnc(d.id)} className="btn-ghost px-3 py-1.5 text-[12px] flex items-center gap-1" data-testid={`dnc-remove-${d.id}`}>
                                        <TrashIcon size={12} weight="bold" /> Remove
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="swiss-section lg:col-span-2" data-testid="audit-panel">
                    <div className="px-5 py-4 border-b border-[var(--border)] flex items-center justify-between gap-3 flex-wrap">
                        <div>
                            <div className="overline">AUDIT LOG</div>
                            <h3 className="text-xl font-semibold tracking-tight mt-0.5">Recent workspace activity</h3>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="overline text-[var(--text-secondary)]">{audit.total} {audit.total === 1 ? "ENTRY" : "ENTRIES"}</span>
                            <button type="button" onClick={() => loadAudit(auditPage)} className="btn-ghost px-2.5 py-1.5 text-[12px] flex items-center gap-1.5" data-testid="audit-refresh">
                                <ArrowsClockwiseIcon size={12} weight="bold" /> Refresh
                            </button>
                        </div>
                    </div>
                    {audit.items.length === 0 && !auditLoading ? (
                        <div className="p-8 text-center text-[var(--text-secondary)] text-[13px]">No audit entries yet.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-[13px] min-w-[720px]" data-testid="audit-table">
                                <thead>
                                    <tr className="border-b border-[var(--border)] [&>th]:py-2.5 [&>th]:px-4 [&>th]:overline [&>th]:text-[10px] [&>th]:text-left">
                                        <th>When</th>
                                        <th>Actor</th>
                                        <th>Action</th>
                                        <th>Target</th>
                                        <th>Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {audit.items.map((a) => {
                                        const color = ACTION_COLORS[a.action] || "#111827";
                                        const meta = a.metadata && Object.keys(a.metadata).length > 0
                                            ? Object.entries(a.metadata)
                                                  .map(([k, v]) => `${k}=${typeof v === "string" ? v : JSON.stringify(v)}`)
                                                  .join(" · ")
                                            : "—";
                                        return (
                                            <tr key={a.id} className="border-b border-[var(--border)] hover:bg-[var(--surface-active)] last:border-b-0" data-testid={`audit-row-${a.id}`}>
                                                <td className="py-2.5 px-4 mono text-[12px] text-[var(--text-secondary)] whitespace-nowrap" title={formatDate(a.timestamp)}>
                                                    {formatRelative(a.timestamp)}
                                                </td>
                                                <td className="py-2.5 px-4 mono text-[12px] truncate max-w-[180px]" title={a.actor_email}>{a.actor_email}</td>
                                                <td className="py-2.5 px-4 whitespace-nowrap">
                                                    <Pill color={color}>{a.action.replace(/_/g, " ")}</Pill>
                                                </td>
                                                <td className="py-2.5 px-4 mono text-[12px] whitespace-nowrap">
                                                    <span className="text-[var(--text-primary)]">{a.target_type}</span>
                                                    {a.target_id && (
                                                        <span className="text-[var(--text-secondary)]">  ·  {a.target_id.slice(0, 8)}…</span>
                                                    )}
                                                </td>
                                                <td className="py-2.5 px-4 mono text-[11px] text-[var(--text-secondary)] truncate max-w-[280px]" title={meta}>
                                                    {meta}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {audit.total > 0 && (
                        <div className="px-5 py-3 border-t border-[var(--border)] flex items-center justify-between text-[12px]" data-testid="audit-pagination">
                            <span className="mono text-[var(--text-secondary)]">
                                Showing {audit.items.length === 0 ? 0 : auditPage * PAGE_SIZE + 1}–
                                {Math.min((auditPage + 1) * PAGE_SIZE, audit.total)} of {audit.total}
                            </span>
                            <div className="flex items-center gap-1.5">
                                <button
                                    type="button"
                                    onClick={() => goToAuditPage(Math.max(auditPage - 1, 0))}
                                    disabled={auditPage === 0 || auditLoading}
                                    className="btn-ghost px-2.5 py-1.5 text-[12px] flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid="audit-prev"
                                >
                                    <CaretLeftIcon size={12} weight="bold" /> Prev
                                </button>
                                <span className="mono text-[11px] text-[var(--text-secondary)] px-2">
                                    {auditPage + 1} / {Math.max(Math.ceil(audit.total / PAGE_SIZE), 1)}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => goToAuditPage((auditPage + 1) * PAGE_SIZE < audit.total ? auditPage + 1 : auditPage)}
                                    disabled={(auditPage + 1) * PAGE_SIZE >= audit.total || auditLoading}
                                    className="btn-ghost px-2.5 py-1.5 text-[12px] flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid="audit-next"
                                >
                                    Next <CaretRightIcon size={12} weight="bold" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Dialog open={!!refreshTarget} onOpenChange={(v) => !v && setRefreshTarget(null)}>
                <DialogContent className="rounded-[2px] sm:max-w-[520px]" data-testid="twilio-refresh-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ArrowsClockwiseIcon size={18} weight="bold" />
                            Refresh Twilio credentials
                        </DialogTitle>
                        <DialogDescription>
                            Rotated your Twilio Auth Token? Paste the current SID + Auth Token below. We&apos;ll verify with Twilio, delete the stale-cred entry in Vapi, and re-import <span className="mono">{refreshTarget?.number}</span> — atomically.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <label className="overline mb-1.5 block">Twilio Account SID</label>
                            <input
                                value={refreshForm.account_sid}
                                onChange={(e) => setRefreshForm({ ...refreshForm, account_sid: e.target.value })}
                                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                                className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] text-[13px] mono focus:border-[var(--accent)] outline-none"
                                data-testid="refresh-sid"
                                autoFocus
                            />
                        </div>
                        <div>
                            <label className="overline mb-1.5 block">Twilio Auth Token (current)</label>
                            <input
                                type="password"
                                value={refreshForm.auth_token}
                                onChange={(e) => setRefreshForm({ ...refreshForm, auth_token: e.target.value })}
                                placeholder="32-character token"
                                className="w-full px-3 py-2 border border-[var(--border)] rounded-[2px] text-[13px] mono focus:border-[var(--accent)] outline-none"
                                data-testid="refresh-token"
                            />
                            <div className="text-[11px] text-[var(--text-secondary)] mt-1">
                                Twilio Console → Account → API keys &amp; tokens → Primary Auth Token.
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <button type="button" onClick={() => setRefreshTarget(null)} className="btn-ghost px-4 py-2 text-[13px]" disabled={refreshBusy} data-testid="refresh-cancel">Cancel</button>
                        <button type="button" onClick={submitRefresh} className="btn-primary px-4 py-2 text-[13px] flex items-center gap-2" disabled={refreshBusy} data-testid="refresh-submit">
                            <ArrowsClockwiseIcon size={14} weight="bold" /> {refreshBusy ? "Refreshing…" : "Verify & refresh"}
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const Integration = ({ id, name, description, connected, badge }) => (
    <li className="px-5 py-4 flex items-center gap-4" data-testid={`integration-${id}`}>
        <div className={`w-9 h-9 flex items-center justify-center ${connected ? "bg-[var(--success)]" : "bg-[var(--surface-active)] border border-[var(--border)]"}`} aria-hidden="true">
            {connected ? <CheckCircleIcon size={18} weight="bold" className="text-white" /> : <XCircleIcon size={18} weight="bold" className="text-[var(--text-secondary)]" />}
        </div>
        <div className="flex-1">
            <div className="font-semibold text-[14px] flex items-center gap-2">
                {name}
                {badge && <span className="overline px-1.5 py-0.5 border border-[var(--warning)] text-[var(--warning)] text-[9px]">{badge}</span>}
            </div>
            <div className="text-[12px] text-[var(--text-secondary)] mt-0.5">{description}</div>
        </div>
        <Pill color={connected ? "#059669" : "#6B7280"}>{connected ? "Connected" : "Not connected"}</Pill>
    </li>
);
